import React from "react";
import {
  Button,
  Spinner,
  Tooltip,
  Checkbox,
  Badge,
} from "@fluentui/react-components";
import {
  ReceiptMoneyRegular,
  DismissRegular,
  CheckmarkCircleFilled,
  DocumentCheckmarkRegular,
  ReceiptAddRegular,
} from "@fluentui/react-icons";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "hooks/useAppTheme";
import {
  proposalsPageRowsState,
  proposalsPageSelectedItemsState,
} from "store/ProposalsPageStore";
import { useTaxonomy } from "library/utils/useTaxonomy";
import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@fluentui/react-components";
import api from "service/api";
import { useLoadOffers } from "store/ProposalsPageHooks";
import {
  CheckPoliciesDialog,
  buildOffersWithPaidInvoices,
} from "./CheckPoliciesDialog";
import { CreateGroupInvoiceDialog } from "./CreateGroupInvoiceDialog";
import { OfferAzureSearch } from "library/types/types";
import { getSearchRowInvoicePayer } from "components/InvoicePayer";

export const SELECTION_TOOLBAR_HEIGHT = 48;

// --- Types ---

interface BankStatementEntry {
  bankStatementEntryId: number;
  amount: number;
  bookingDate: string;
  clientName: string | null;
  clientCode: string | null;
  paymentReferenceNumber: string | null;
  isCleared: boolean;
}

interface OfferInvoice {
  offerId: string;
  invoiceNumber: string | null;
  clientCode: string;
  clientName: string;
  invoiceMtplPremium: number;
  invoiceCascoPremium: number;
  invoiceDate: string;
  selectedInsurer: string | null;
  /** the invoice number / premium belong to the group invoice shared by several offers */
  isGroupInvoice: boolean;
}

// --- Helpers ---

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "-";
  return dateStr.split("T")[0] ?? dateStr;
};

const formatAmount = (amount: number): string => {
  return amount.toLocaleString("en", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** GROUP_INVOICE / GROUP_INVOICE_SENT / GROUP_INVOICE_PAID: the offer is settled through its group invoice */
const isGroupInvoiceStatusCode = (code: string | undefined): boolean =>
  !!code && code.startsWith("OfferStatus.GROUP_INVOICE");

/** A grouped offer is settled through its group invoice */
const isGroupedRow = (
  item: OfferAzureSearch,
  offerStatusCode: string | undefined,
): boolean => isGroupInvoiceStatusCode(offerStatusCode) && !!item.GroupInvoiceNumber;

// --- Component ---

type Props = {
  onClearSelection?: () => void;
};

export const SelectionToolbar = ({ onClearSelection }: Props) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const rawSelectedItems = useAtomValue(proposalsPageSelectedItemsState);
  const rows = useAtomValue(proposalsPageRowsState);
  // The selection keeps the row objects from the moment of selection; after the list is
  // reloaded (e.g. invoice marked as paid) use the fresh row so statuses/flags are current
  const selectedItems = useMemo(
    () =>
      rawSelectedItems.map(
        (item) => rows.find((r) => r?.OfferId === item?.OfferId) ?? item,
      ),
    [rawSelectedItems, rows],
  );
  const taxonomy = useTaxonomy();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPoliciesDialogOpen, setIsPoliciesDialogOpen] = useState(false);
  const [isGroupInvoiceConfirmOpen, setIsGroupInvoiceConfirmOpen] = useState(false);
  const [isGroupInvoiceDialogOpen, setIsGroupInvoiceDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bankEntries, setBankEntries] = useState<BankStatementEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [checkedOffers, setCheckedOffers] = useState<Set<string>>(new Set());
  const [isMarking, setIsMarking] = useState(false);
  // Per-offer result: offerId -> error message or null (success)
  const [markResults, setMarkResults] = useState<Record<string, string | null>>({});
  const loadOffers = useLoadOffers();

  const hasSelection = selectedItems.length > 0;

  // Filter selected items that have invoice status
  // offers marked as error take part in no operation
  const invoiceEligibleItems = selectedItems.filter((item) => {
    if (!item || !taxonomy || item.IsMarkedAsError) return false;
    const offerStatusCode =
      taxonomy.OfferStatus?.byId?.[item.OfferStatus || 0]?.code;
    const hasInvoiceStatus =
      offerStatusCode === "OfferStatus.INVOICE" ||
      offerStatusCode === "OfferStatus.INVOICE_SENT";
    const hasUnpaidInvoice =
      item.InvoiceMtplIsPaid === false || item.InvoiceCascoIsPaid === false;
    // a grouped offer is settled through its group invoice
    const hasUnpaidGroupInvoice =
      isGroupInvoiceStatusCode(offerStatusCode) &&
      !!item.GroupInvoiceNumber &&
      item.GroupInvoiceIsPaid !== true;
    return (hasInvoiceStatus && hasUnpaidInvoice) || hasUnpaidGroupInvoice;
  });

  // Selected items with a paid invoice (status INVOICE_PAID) and a VIN - candidates for "Check policies"
  const paidInvoiceEligibleItems = selectedItems.filter((item) => {
    if (!item || !taxonomy || item.IsMarkedAsError) return false;
    const offerStatusCode =
      taxonomy.OfferStatus?.byId?.[item.OfferStatus || 0]?.code;
    return (
      (offerStatusCode === "OfferStatus.INVOICE_PAID" ||
        offerStatusCode === "OfferStatus.GROUP_INVOICE_PAID") &&
      buildOffersWithPaidInvoices([item]).length > 0
    );
  });

  // Selected items in status INVOICE (or GROUP_INVOICE, regrouping) with a line invoice - candidates for one group invoice.
  // The action is offered only for at least two rows and when every selected row qualifies, so it is clear what gets grouped.
  const groupInvoiceEligibleItems = selectedItems.filter((item) => {
    if (!item || !taxonomy || item.IsMarkedAsError) return false;
    const offerStatusCode =
      taxonomy.OfferStatus?.byId?.[item.OfferStatus || 0]?.code;
    return (
      (offerStatusCode === "OfferStatus.INVOICE" ||
        offerStatusCode === "OfferStatus.GROUP_INVOICE") &&
      !!(item.InvoiceMtplInvoiceNumber || item.InvoiceCascoInvoiceNumber)
    );
  });
  const canCreateGroupInvoice =
    selectedItems.length > 1 &&
    groupInvoiceEligibleItems.length === selectedItems.length;

  // Build payload: { invoice payer code: invoiceDate } - the payer of the invoice the offer is settled through
  const buildPayload = () => {
    const payload: Record<string, string> = {};
    for (const item of invoiceEligibleItems) {
      const offerStatusCode =
        taxonomy?.OfferStatus?.byId?.[item.OfferStatus || 0]?.code;
      const code = getSearchRowInvoicePayer(
        item,
        isGroupedRow(item, offerStatusCode),
      ).code;
      const invoiceDate =
        item.InvoiceCascoInvoiceIssuedAt || item.InvoiceMtplInvoiceIssuedAt;
      if (code && invoiceDate) {
        const dateOnly = invoiceDate.split("T")[0];
        if (!payload[code] || dateOnly < payload[code]) {
          payload[code] = dateOnly;
        }
      }
    }
    return payload;
  };

  // Build offers directly from selected items — no extra API calls
  const offers: OfferInvoice[] = useMemo(() => {
    return invoiceEligibleItems.map((item) => {
      const offerStatusCode =
        taxonomy?.OfferStatus?.byId?.[item.OfferStatus || 0]?.code;
      const isGroupInvoice = isGroupedRow(item, offerStatusCode);
      // bank payments are matched by the payer of the invoice the offer is settled through
      const invoicePayer = getSearchRowInvoicePayer(item, isGroupInvoice);
      return {
        offerId: item.OfferId,
        invoiceNumber: isGroupInvoice
          ? item.GroupInvoiceNumber
          : item.InvoiceMtplInvoiceNumber || item.InvoiceCascoInvoiceNumber || null,
        clientCode: invoicePayer.code,
        clientName: item.ClientName || invoicePayer.name,
        // for a grouped offer the payment covers the whole group invoice
        invoiceMtplPremium: isGroupInvoice
          ? (item.GroupInvoicePremium ?? 0)
          : (item.InvoiceMtplPremium ?? 0),
        invoiceCascoPremium: isGroupInvoice ? 0 : (item.InvoiceCascoPremium ?? 0),
        invoiceDate:
          (
            (isGroupInvoice ? item.GroupInvoiceIssuedAt : null) ||
            item.InvoiceCascoInvoiceIssuedAt ||
            item.InvoiceMtplInvoiceIssuedAt ||
            ""
          ).split("T")[0] ?? "",
        selectedInsurer:
          item.InvoiceCascoInsurer || item.InvoiceMtplInsurer || null,
        isGroupInvoice,
      };
    });
  }, [invoiceEligibleItems, taxonomy]);

  // Match bank statement entries to each offer by clientCode
  const offersWithEntries = useMemo(() => {
    return offers.map((offer) => {
      const matchingEntries = bankEntries.filter(
        (entry) => entry.clientCode === offer.clientCode,
      );
      return { ...offer, entries: matchingEntries };
    });
  }, [offers, bankEntries]);

  // Fetch potential payments when dialog opens
  useEffect(() => {
    if (!isDialogOpen) return;

    const fetchPayments = async () => {
      setIsLoading(true);
      setError(null);
      setBankEntries([]);
      setCheckedOffers(new Set());
      setMarkResults({});
      try {
        const payload = buildPayload();
        const response = await api.patch(
          "/invoices/getpotentialpayments",
          payload,
        );
        const entries: BankStatementEntry[] = response.data || [];
        setBankEntries(entries);

        // Auto-check offers that have an entry with exactly matching amount
        const autoChecked = new Set<string>();
        for (const offer of offers) {
          const totalPremium = offer.invoiceMtplPremium + offer.invoiceCascoPremium;
          if (
            entries.some(
              (e) =>
                e.clientCode === offer.clientCode &&
                e.amount === totalPremium,
            )
          ) {
            autoChecked.add(offer.offerId);
          }
        }
        setCheckedOffers(autoChecked);
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch potential payments",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDialogOpen]);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (!isLoading && !isMarking) {
      const hadResults = Object.keys(markResults).length > 0;
      setIsDialogOpen(false);
      setBankEntries([]);
      setError(null);
      setCheckedOffers(new Set());
      setMarkResults({});
      if (hadResults) {
        loadOffers({ skip: 0, top: 40, reset: true });
      }
    }
  };

  const toggleOffer = (offerId: string) => {
    setCheckedOffers((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  };

  const handleMarkAsPaid = async () => {
    setIsMarking(true);
    setMarkResults({});
    try {
      // a group invoice is marked once (the backend settles all its offers), so members sharing a number are sent once
      const sentGroupNumbers = new Set<string>();
      const checked = offers.filter((o) => checkedOffers.has(o.offerId));
      const payload = checked
        .filter((o) => {
          if (!o.isGroupInvoice || !o.invoiceNumber) return true;
          if (sentGroupNumbers.has(o.invoiceNumber)) return false;
          sentGroupNumbers.add(o.invoiceNumber);
          return true;
        })
        .map((o) => ({
          OfferId: o.offerId,
          InvoiceNumber: o.invoiceNumber,
        }));
      const response = await api.patch("/invoices/markaspaid", payload);
      const results: any[] = response.data || [];

      // Build per-offer result map — include all offers, not just API results
      const resultMap: Record<string, string | null> = {};

      // First, mark all unchecked offers as skipped
      for (const offer of offers) {
        if (!checkedOffers.has(offer.offerId)) {
          resultMap[offer.offerId] = "skipped";
        }
      }

      // Then apply API results for checked offers
      for (const item of results) {
        const id = item.OfferId || item.offerId || "";
        const msg =
          item.ErrorMessage || item.errorMessage || item.error || null;
        resultMap[id] = msg;
      }

      // Members of a group invoice share the result of the member that was sent
      for (const offer of checked) {
        if (offer.offerId in resultMap || !offer.isGroupInvoice) continue;
        const sent = checked.find(
          (o) =>
            o.isGroupInvoice &&
            o.invoiceNumber === offer.invoiceNumber &&
            o.offerId in resultMap,
        );
        resultMap[offer.offerId] = sent ? (resultMap[sent.offerId] ?? null) : null;
      }
      setMarkResults(resultMap);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to mark invoices as paid",
      );
    } finally {
      setIsMarking(false);
    }
  };

  const thStyle: React.CSSProperties = {
    padding: "6px 8px",
    textAlign: "left",
    fontWeight: 600,
    color: theme.semanticColors.bodySubtext,
    borderBottom: `2px solid ${theme.semanticColors.bodyDivider}`,
    position: "sticky",
    top: 0,
    background: theme.semanticColors.bodyFrameBackground,
    zIndex: 1,
  };

  const tdStyle: React.CSSProperties = {
    padding: "4px 8px",
    borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
    verticalAlign: "middle",
  };

  if (!hasSelection) return null;

  return (
    <>
      <div
        style={{
          height: SELECTION_TOOLBAR_HEIGHT,
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "0 16px",
          borderTop: `1px solid ${theme.semanticColors.bodyDivider}`,
          background: theme.semanticColors.bodyStandoutBackground,
          flexShrink: 0,
        }}
      >
        <span
          data-help="offers-selection-count"
          style={{
            fontWeight: 600,
            color: theme.semanticColors.bodyText,
            marginRight: 8,
          }}
        >
          {t("qtg.selectionToolbar.selectedCount", {
            count: selectedItems.length,
            defaultValue: "{{count}} selected",
          })}
        </span>

        <Tooltip
          content={
            invoiceEligibleItems.length === 0
              ? t(
                  "qtg.selectionToolbar.noEligibleInvoices",
                  "No selected items have unpaid invoices",
                )
              : t("qtg.contextMenu.checkPayments", "Check payments")
          }
          relationship="label"
        >
          <Button
            data-help="offers-check-payments"
            appearance="primary"
            icon={<ReceiptMoneyRegular />}
            disabled={invoiceEligibleItems.length === 0}
            onClick={handleOpenDialog}
            size="small"
          >
            {t("qtg.contextMenu.checkPayments", "Check payments")}
            {invoiceEligibleItems.length > 0 &&
              ` (${invoiceEligibleItems.length})`}
          </Button>
        </Tooltip>

        <Tooltip
          content={
            paidInvoiceEligibleItems.length === 0
              ? t(
                  "qtg.selectionToolbar.noEligiblePaidInvoices",
                  "No selected items have paid invoices with a VIN",
                )
              : t("qtg.contextMenu.checkPolicies", "Check policies")
          }
          relationship="label"
        >
          <Button
            data-help="offers-check-policies"
            appearance="primary"
            icon={<DocumentCheckmarkRegular />}
            disabled={paidInvoiceEligibleItems.length === 0}
            onClick={() => setIsPoliciesDialogOpen(true)}
            size="small"
          >
            {t("qtg.contextMenu.checkPolicies", "Check policies")}
            {paidInvoiceEligibleItems.length > 0 &&
              ` (${paidInvoiceEligibleItems.length})`}
          </Button>
        </Tooltip>

        <Tooltip
          content={
            !canCreateGroupInvoice
              ? t(
                  "qtg.selectionToolbar.noEligibleGroupInvoice",
                  "Select at least two items, all in status Invoice with an existing invoice",
                )
              : t("qtg.groupInvoice.button", "Create group invoice")
          }
          relationship="label"
        >
          <Button
            data-help="offers-create-group-invoice"
            appearance="primary"
            icon={<ReceiptAddRegular />}
            disabled={!canCreateGroupInvoice}
            onClick={() => setIsGroupInvoiceConfirmOpen(true)}
            size="small"
          >
            {t("qtg.groupInvoice.button", "Create group invoice")}
            {canCreateGroupInvoice && ` (${selectedItems.length})`}
          </Button>
        </Tooltip>

        <div style={{ marginLeft: "auto" }}>
          <Button
            data-help="offers-clear-selection"
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={onClearSelection}
            size="small"
          >
            {t("qtg.selectionToolbar.clearSelection", "Clear selection")}
          </Button>
        </div>
      </div>

      {/* Group invoice: confirmation first, then the dialog with number, due date and the invoice lines */}
      <Dialog
        open={isGroupInvoiceConfirmOpen}
        onOpenChange={(e, data) => !data.open && setIsGroupInvoiceConfirmOpen(false)}
      >
        <DialogSurface style={{ maxWidth: "480px" }}>
          <DialogBody>
            <DialogTitle>
              {t("qtg.groupInvoice.confirm.title", "Create group invoice")}
            </DialogTitle>
            <DialogContent>
              {t("qtg.groupInvoice.confirm.message", {
                count: selectedItems.length,
                defaultValue:
                  "Do you want to create one group invoice for the {{count}} selected offer(s)?",
              })}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setIsGroupInvoiceConfirmOpen(false)}
              >
                {t("qtg.common.cancel", "Cancel")}
              </Button>
              <Button
                appearance="primary"
                icon={<ReceiptAddRegular />}
                onClick={() => {
                  setIsGroupInvoiceConfirmOpen(false);
                  setIsGroupInvoiceDialogOpen(true);
                }}
              >
                {t("qtg.groupInvoice.confirm.continue", "Continue")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <CreateGroupInvoiceDialog
        open={isGroupInvoiceDialogOpen}
        items={groupInvoiceEligibleItems}
        onClose={(created) => {
          setIsGroupInvoiceDialogOpen(false);
          if (created) {
            onClearSelection?.();
            loadOffers({ skip: 0, top: 40, reset: true });
          }
        }}
      />

      <CheckPoliciesDialog
        open={isPoliciesDialogOpen}
        items={paidInvoiceEligibleItems}
        onClose={(hadResults) => {
          setIsPoliciesDialogOpen(false);
          // the reviewed offers are no longer needed in the selection
          onClearSelection?.();
          if (hadResults) {
            loadOffers({ skip: 0, top: 40, reset: true });
          }
        }}
      />

      <Dialog
        open={isDialogOpen}
        onOpenChange={(e, data) => !data.open && handleCloseDialog()}
      >
        <DialogSurface style={{ maxWidth: "90vw", width: "1000px" }}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<DismissRegular />}
                  onClick={handleCloseDialog}
                  disabled={isLoading || isMarking}
                />
              }
            >
              {t("qtg.checkPayments.dialog.title", "Check payments")}
            </DialogTitle>
            <DialogContent>
              {/* Loading state */}
              {isLoading && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "48px 0",
                  }}
                >
                  <Spinner
                    size="large"
                    label={t("qtg.common.processing", "Processing...")}
                  />
                </div>
              )}

              {/* Error */}
              {error && (
                <div
                  style={{
                    color: theme.semanticColors.errorText,
                    padding: "8px 0",
                  }}
                >
                  {error}
                </div>
              )}

              {/* Mark as paid results are rendered inline in the table */}

              {/* Offer list with matched bank entries */}
              {!isLoading && bankEntries.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    maxHeight: "60vh",
                    overflow: "auto",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 13,
                      color: theme.semanticColors.bodyText,
                      tableLayout: "fixed",
                    }}
                  >
                    <colgroup>
                      <col style={{ width: 32 }} />
                      <col style={{ width: 100 }} />
                      <col />
                      <col style={{ width: 120 }} />
                      <col style={{ width: 130 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 110 }} />
                      <col />
                      <col style={{ width: 80 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={thStyle}></th>
                        <th style={thStyle}>
                          {t("qtg.potentialPayments.offerId", "Offer ID")}
                        </th>
                        <th style={thStyle}>
                          {t("qtg.potentialPayments.client", "Client")}
                        </th>
                        <th style={thStyle}>
                          {t("qtg.potentialPayments.insurer", "Insurer")}
                        </th>
                        <th style={thStyle}>
                          {t("qtg.potentialPayments.invoiceNumber", "Invoice #")}
                        </th>
                        <th style={{ ...thStyle, textAlign: "right" }}>
                          {t("qtg.potentialPayments.amount", "Amount")}
                        </th>
                        <th style={thStyle}>
                          {t("qtg.potentialPayments.date", "Date")}
                        </th>
                        <th style={thStyle}>
                          {t("qtg.potentialPayments.reference", "Reference")}
                        </th>
                        <th style={thStyle}>
                          {t("qtg.potentialPayments.status", "Status")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {offersWithEntries.map((offer, idx) => (
                        <React.Fragment key={offer.offerId}>
                          {/* Spacer between offer sections */}
                          {idx > 0 && (
                            <tr>
                              <td
                                colSpan={9}
                                style={{ height: 8, border: "none" }}
                              />
                            </tr>
                          )}
                          {/* Offer row */}
                          <tr
                            style={{
                              background:
                                theme.semanticColors.bodyStandoutBackground,
                            }}
                          >
                            <td style={tdStyle}>
                              <Checkbox
                                checked={checkedOffers.has(offer.offerId)}
                                onChange={() => toggleOffer(offer.offerId)}
                                disabled={isMarking || Object.keys(markResults).length > 0}
                              />
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>
                              {offer.offerId}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={offer.clientName}
                            >
                              {offer.clientName}
                            </td>
                            <td style={tdStyle}>
                              {offer.selectedInsurer ? (
                                <Badge appearance="outline" size="small">
                                  {offer.selectedInsurer}
                                </Badge>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td style={tdStyle}>
                              {offer.invoiceNumber || "-"}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: "right",
                                fontWeight: 600,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              {formatAmount(offer.invoiceMtplPremium + offer.invoiceCascoPremium)}
                            </td>
                            <td style={tdStyle}>{offer.invoiceDate}</td>
                            <td style={tdStyle}></td>
                            <td style={tdStyle}></td>
                          </tr>
                          {/* Bank statement entry rows */}
                          {offer.entries.map((entry) => (
                            <tr key={entry.bankStatementEntryId}>
                              <td style={tdStyle}></td>
                              <td style={tdStyle}></td>
                              <td
                                style={{
                                  ...tdStyle,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  color: theme.semanticColors.bodySubtext,
                                }}
                                title={entry.clientName || ""}
                              >
                                {entry.clientName || "-"}
                              </td>
                              <td style={tdStyle}></td>
                              <td style={tdStyle}></td>
                              <td
                                style={{
                                  ...tdStyle,
                                  textAlign: "right",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  {entry.amount === (offer.invoiceMtplPremium + offer.invoiceCascoPremium) && (
                                    <CheckmarkCircleFilled
                                      style={{ color: theme.semanticColors.successIcon, fontSize: 16 }}
                                    />
                                  )}
                                  {formatAmount(entry.amount)}
                                </span>
                              </td>
                              <td style={tdStyle}>
                                {formatDate(entry.bookingDate)}
                              </td>
                              <td
                                style={{
                                  ...tdStyle,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={entry.paymentReferenceNumber || ""}
                              >
                                {entry.paymentReferenceNumber || "-"}
                              </td>
                              <td style={tdStyle}>
                                {entry.isCleared ? (
                                  <Badge
                                    appearance="filled"
                                    color="success"
                                    size="small"
                                  >
                                    {t(
                                      "qtg.potentialPayments.cleared",
                                      "Cleared",
                                    )}
                                  </Badge>
                                ) : (
                                  <Badge
                                    appearance="filled"
                                    color="warning"
                                    size="small"
                                  >
                                    {t(
                                      "qtg.potentialPayments.uncleared",
                                      "Uncleared",
                                    )}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                          {/* No matching entries */}
                          {offer.entries.length === 0 && (
                            <tr>
                              <td style={tdStyle}></td>
                              <td
                                colSpan={8}
                                style={{
                                  ...tdStyle,
                                  color: theme.semanticColors.bodySubtext,
                                  fontStyle: "italic",
                                }}
                              >
                                {t(
                                  "qtg.potentialPayments.noMatchingEntries",
                                  "No matching bank statement entries",
                                )}
                              </td>
                            </tr>
                          )}
                          {/* Per-offer mark result */}
                          {offer.offerId in markResults && (
                            <tr>
                              <td style={tdStyle}></td>
                              <td
                                colSpan={8}
                                style={{
                                  ...tdStyle,
                                  padding: "8px",
                                  background:
                                    markResults[offer.offerId] === "skipped"
                                      ? theme.semanticColors.bodyStandoutBackground
                                      : markResults[offer.offerId]
                                        ? theme.semanticColors.errorBackground
                                        : theme.semanticColors.successBackground,
                                  color:
                                    markResults[offer.offerId] === "skipped"
                                      ? theme.semanticColors.bodySubtext
                                      : markResults[offer.offerId]
                                        ? theme.semanticColors.errorText
                                        : theme.semanticColors.successText,
                                  borderRadius: 2,
                                  fontStyle:
                                    markResults[offer.offerId] === "skipped"
                                      ? "italic"
                                      : undefined,
                                }}
                              >
                                {markResults[offer.offerId] === "skipped"
                                  ? t(
                                      "qtg.checkPayments.skipped",
                                      "Skipped — not selected",
                                    )
                                  : markResults[offer.offerId]
                                    ? markResults[offer.offerId]
                                    : t(
                                        "qtg.checkPayments.success",
                                        "Invoice marked as paid successfully",
                                      )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* No entries at all */}
              {!isLoading && bankEntries.length === 0 && !error && (
                <div
                  style={{
                    padding: "32px 0",
                    textAlign: "center",
                    color: theme.semanticColors.bodySubtext,
                  }}
                >
                  {t(
                    "qtg.potentialPayments.noEntries",
                    "No potential payments found",
                  )}
                </div>
              )}
            </DialogContent>

            {/* Footer actions */}
            <DialogActions>
              {Object.keys(markResults).length === 0 &&!isLoading && bankEntries.length > 0 && (
                <Button
                  appearance="primary"
                  icon={
                    isMarking ? (
                      <Spinner size="tiny" />
                    ) : (
                      <ReceiptMoneyRegular />
                    )
                  }
                  disabled={checkedOffers.size === 0 || isMarking}
                  onClick={handleMarkAsPaid}
                >
                  {isMarking
                    ? t("qtg.common.processing", "Processing...")
                    : t("qtg.checkPayments.dialog.confirm", "Mark invoice(s) as paid")}
                  {!isMarking &&
                    checkedOffers.size > 0 &&
                    ` (${checkedOffers.size})`}
                </Button>
              )}
              {Object.keys(markResults).length > 0 && (
                <Button appearance="primary" onClick={handleCloseDialog}>
                  {t("qtg.common.close", "Close")}
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
